import type { FC } from 'react';
import { lazy, Suspense, useEffect } from 'react';

import CopyrightFooter from 'src/app/components/Elements/CopyrightFooter';
import { VerticalProgressBar } from 'src/app/components/Elements/ProgressBars/VerticalProgressBar/index';
import Spinner from 'src/app/components/Elements/Spinner';
import {
  Content,
  FooterRow,
  MainRow,
  MainSection,
  OnboardingContainer,
  Sidebar,
} from 'src/app/components/Pages/GetStarted/styles';
import { loggedInPromoOnboardingPage } from 'src/app/router/paths';

import type { PropsFromRedux } from './container';
import type { OnboardingStep } from './StepLayout/utils';

export const PromoOnboardingStepsRenderer: FC<PropsFromRedux> = ({
  currentStepIndex,
  navigateToOnboardingStep,
  previousPathname,
  promoOnboardingSteps,
}) => {
  useEffect(() => {
    // Note: Scroll to top only on step change
    window.scrollTo(0, 0);
  }, [currentStepIndex]);

  // When mounting from another page, reset onboarding steps to the beginning
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (previousPathname !== loggedInPromoOnboardingPage.toPath()) {
      navigateToOnboardingStep(0);
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const labels = promoOnboardingSteps.map((step: OnboardingStep) => step.label);
  const stepComponent = promoOnboardingSteps[currentStepIndex];
  const StepComponent = lazy(() => import(`${stepComponent.component}`));

  return (
    <OnboardingContainer>
      <MainRow smallBottomPadding>
        <Sidebar md="3" lg="3" xl="4">
          <VerticalProgressBar
            currentStep={currentStepIndex + 1}
            totalSteps={promoOnboardingSteps.length}
            labels={labels}
            height={promoOnboardingSteps.length < 2 ? 4 : 9.6}
            handleLabelClick={navigateToOnboardingStep}
          />
        </Sidebar>
        <MainSection>
          <Content>
            <Suspense fallback={<Spinner centered />}>
              <StepComponent />
            </Suspense>
          </Content>
        </MainSection>
      </MainRow>
      <FooterRow>
        <CopyrightFooter isAlignedRight textColor={'gray'} />
      </FooterRow>
    </OnboardingContainer>
  );
};
